import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowRight, ShoppingCart, Filter, Ruler, CheckCircle2, AlertCircle, HelpCircle, CircleDot } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbSeparator, BreadcrumbPage } from '../components/ui/breadcrumb';
import SEO from '../components/SEO';
import CatalogSearchBar from '../components/CatalogSearchBar';
import ContactCTA from '../components/ContactCTA';
import HelpBlock from '../components/HelpBlock';
import axios from 'axios';
import { formatPrice } from '../utils/formatPrice';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL = "contact@atlantiquespaboutique.com";

const addToCart = (product) => {
  const cart = JSON.parse(localStorage.getItem('spa_cart') || '[]');
  const existingIndex = cart.findIndex(item => item.slug === product.slug);
  if (existingIndex >= 0) {
    cart[existingIndex].quantity = (cart[existingIndex].quantity || 1) + 1;
  } else {
    cart.push({
      slug: product.slug,
      name: product.name,
      price: product.price_ttc,
      image: product.images?.[0] || '',
      quantity: 1,
      category: 'filtres'
    });
  }
  localStorage.setItem('spa_cart', JSON.stringify(cart));
  window.dispatchEvent(new Event('cartUpdated'));
};

const FiltresSpaPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchResults, setSearchResults] = useState(null);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${API}/parts`);
        const filtres = response.data.filter(p => 
          p.main_category === 'filtres-spa' ||
          p.sub_category === 'filtres' || 
          (p.name?.toLowerCase().includes('filtre') && !p.name?.toLowerCase().includes('nettoyant'))
        );
        setProducts(filtres);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const handleSearchResults = (results) => {
    setSearchResults(results);
  };

  const displayedProducts = searchResults !== null ? searchResults : products;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "Filtres de spa – Cartouches de filtration",
    "description": "Filtres et cartouches de filtration pour spa. Compatible Jacuzzi, Sundance, Canadian Spa, Wellis. Livraison France.",
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Accueil", "item": "https://atlantiquespaboutique.com/" },
        { "@type": "ListItem", "position": 2, "name": "Pièces & Accessoires", "item": "https://atlantiquespaboutique.com/pieces-accessoires" },
        { "@type": "ListItem", "position": 3, "name": "Filtres spa", "item": "https://atlantiquespaboutique.com/filtres-spa" }
      ]
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="filtres-spa-page">
      <SEO 
        title="Filtres de spa – Cartouches compatibles Jacuzzi, Sundance, Wellis | Atlantique Spa Boutique"
        description="Filtres et cartouches de filtration pour spa. Compatible Jacuzzi, Sundance, Canadian Spa, Wellis, Arctic Spas. Dimensions vérifiées. Livraison France."
        canonical="/filtres-spa"
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />

      {/* Breadcrumb */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/">Accueil</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink href="/pieces-accessoires">Pièces & Accessoires</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Filtres spa</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-5 sm:py-6">
        
        {/* H1 — discret, SEO only */}
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="filtres-header">
          Filtres de spa
        </h1>

        {/* Conseil avant commande — compact */}
        <div className="flex items-start gap-3 bg-emerald-50 border border-emerald-200 rounded-lg p-3 sm:p-4 mb-4" data-testid="advice-section">
          <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
          <p className="text-slate-700 text-xs sm:text-sm leading-relaxed">
            <strong>Conseil :</strong> Vérifiez les dimensions (diamètre, longueur) et le type de fixation (filetage ou emboîtement) de votre ancien filtre avant commande. En cas de doute, <a href={`mailto:${EMAIL}`} className="text-[#1E3A5F] font-semibold hover:underline">contactez-nous</a>.
          </p>
        </div>

        {/* Barre de recherche */}
        <div className="mb-5">
          <CatalogSearchBar 
            products={products}
            onSearchResults={handleSearchResults}
            placeholder="Rechercher par référence (ex : PRB50, 6CH-940, C-4950...)"
            category="filtres"
            emptyIcon={Filter}
            showHelpText={false}
          />
        </div>

        {/* Résultats de recherche */}
        {searchResults !== null && (
          <div className="mb-4 flex items-center justify-between bg-[#1E3A5F]/5 rounded-lg px-4 py-2.5">
            <p className="text-slate-700 text-sm">
              <span className="font-semibold">{displayedProducts.length}</span> résultat{displayedProducts.length > 1 ? 's' : ''}
            </p>
            <button 
              onClick={() => setSearchResults(null)}
              className="text-sm text-[#1E3A5F] font-medium hover:underline"
              data-testid="clear-search"
            >
              Effacer
            </button>
          </div>
        )}

        {/* ====== GRILLE PRODUITS — Visible immédiatement ====== */}
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6" data-testid="products-skeleton">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="bg-white rounded-xl p-3 sm:p-4 animate-pulse">
                <div className="h-32 sm:h-40 bg-slate-200 rounded-lg mb-3" />
                <div className="h-4 bg-slate-200 rounded w-3/4 mb-2" />
                <div className="h-4 bg-slate-200 rounded w-1/2" />
              </div>
            ))}
          </div>
        ) : displayedProducts.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
            <Filter className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">
              {searchResults !== null ? 'Aucun filtre trouvé' : 'Filtres bientôt disponibles'}
            </h3>
            <p className="text-slate-600 text-sm mb-4">
              {searchResults !== null 
                ? 'Essayez avec une autre référence.'
                : 'Notre catalogue est en cours de préparation.'
              }
            </p>
            <a href={`mailto:${EMAIL}`}>
              <Button className="bg-[#1E3A5F] hover:bg-[#152C4A]" size="sm">
                Nous contacter
              </Button>
            </a>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6" data-testid="products-grid">
            {displayedProducts.map((product) => (
              <Card key={product.slug} className="overflow-hidden border border-slate-200 hover:border-[#1E3A5F]/30 hover:shadow-lg transition-all group" data-testid={`product-${product.slug}`}>
                <Link to={`/pieces-accessoires/${product.slug}`}>
                  <div className="relative h-32 sm:h-40 bg-slate-100 flex items-center justify-center">
                    {product.images?.[0] ? (
                      <img 
                        src={product.images[0]} 
                        alt={product.name}
                        className="w-full h-full object-contain p-2 sm:p-3 group-hover:scale-105 transition-transform"
                        loading="lazy"
                      />
                    ) : (
                      <Filter className="w-10 h-10 text-slate-300" />
                    )}
                    {product.availability === 'en_stock' && (
                      <span className="absolute top-1.5 right-1.5 bg-emerald-500 text-white text-[10px] sm:text-xs font-medium px-1.5 py-0.5 sm:px-2 sm:py-1 rounded">
                        En stock
                      </span>
                    )}
                  </div>
                </Link>
                <CardContent className="p-3 sm:p-4">
                  {product.brand && (
                    <span className="text-[10px] sm:text-xs text-slate-500 uppercase tracking-wide">{product.brand}</span>
                  )}
                  <Link to={`/pieces-accessoires/${product.slug}`}>
                    <h3 className="font-semibold text-slate-900 text-xs sm:text-sm mt-0.5 mb-2 line-clamp-2 group-hover:text-[#1E3A5F] transition-colors">
                      {product.name}
                    </h3>
                  </Link>
                  {/* Dimensions compactes */}
                  {product.diameter_mm && (
                    <p className="text-[10px] sm:text-xs text-slate-400 mb-1.5 truncate">
                      {product.diameter_mm} mm &times; {product.length_mm} mm
                    </p>
                  )}
                  {product.price_ttc ? (
                    <div className="flex items-center justify-between">
                      <span className="text-sm sm:text-lg font-bold text-slate-900">{formatPrice(product.price_ttc)}</span>
                      <Button 
                        size="sm" 
                        onClick={() => addToCart(product)}
                        className="bg-[#1E3A5F] hover:bg-[#152C4A] h-7 sm:h-8 px-2 sm:px-3"
                        data-testid={`add-cart-${product.slug}`}
                      >
                        <ShoppingCart className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      </Button>
                    </div>
                  ) : (
                    <a href={`mailto:${EMAIL}`}>
                      <Button variant="outline" size="sm" className="w-full text-xs">
                        Demander un prix
                      </Button>
                    </a>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Besoin d'aide — petit bloc discret */}
        <div className="mt-6 mb-2">
          <HelpBlock />
        </div>

        {/* ====== ZONE BASSE : Contenu SEO / Guides — APRÈS les produits ====== */}

        {/* Guide : Comment bien choisir son filtre */}
        <div className="mt-10 bg-white rounded-xl border border-slate-200 p-5 sm:p-6" data-testid="guide-section">
          <h2 className="text-lg sm:text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
            <Ruler className="w-5 h-5 text-[#1E3A5F]" />
            Comment bien choisir son filtre de spa
          </h2>
          
          <p className="text-slate-600 text-sm mb-5">
            Pour trouver le filtre compatible avec votre spa, relevez ces informations sur votre ancien filtre :
          </p>

          <div className="grid md:grid-cols-2 gap-5">
            <div className="bg-slate-50 rounded-lg p-4">
              <h3 className="font-semibold text-slate-900 text-sm mb-2 flex items-center gap-2">
                <CircleDot className="w-3.5 h-3.5 text-[#F97316]" />
                Dimensions essentielles
              </h3>
              <ul className="space-y-1.5 text-slate-600 text-sm">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <span><strong>Diamètre extérieur</strong> : diamètre total du filtre</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <span><strong>Diamètre intérieur</strong> : trou central (haut et bas)</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <span><strong>Hauteur</strong> : longueur totale de la cartouche</span>
                </li>
              </ul>
            </div>

            <div className="bg-slate-50 rounded-lg p-4">
              <h3 className="font-semibold text-slate-900 text-sm mb-2 flex items-center gap-2">
                <CircleDot className="w-3.5 h-3.5 text-[#F97316]" />
                Type de fixation
              </h3>
              <ul className="space-y-1.5 text-slate-600 text-sm">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <span><strong>Avec filetage</strong> : se visse dans le logement</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <span><strong>Sans filetage</strong> : simple emboîtement</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <span><strong>Poignée / ouvert</strong> : vérifier haut et bas</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-xs sm:text-sm text-slate-600 flex items-start gap-2">
              <HelpCircle className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
              <span>Vérifiez aussi la présence d'une <strong>poignée</strong> et la <strong>référence équivalente</strong> inscrite sur votre ancien filtre.</span>
            </p>
          </div>
        </div>

        {/* Texte SEO détaillé */}
        <div className="mt-6 bg-white rounded-xl border border-slate-200 p-5 sm:p-6" data-testid="seo-section">
          <h2 className="text-lg sm:text-xl font-bold text-slate-900 mb-3">Le rôle essentiel du filtre dans votre spa</h2>
          <p className="text-slate-600 text-sm leading-relaxed mb-3">
            Le filtre de votre spa assure la <strong>propreté et la clarté de l'eau</strong> en retenant les impuretés, 
            les débris et les particules fines. Sans un filtre adapté et en bon état, l'eau devient rapidement trouble, 
            le système de filtration perd en efficacité, et les équipements (pompe, réchauffeur) s'usent prématurément.
          </p>
          <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs sm:text-sm text-amber-800">
              <strong>Un filtre inadapté peut :</strong> réduire l'efficacité de la filtration, provoquer des pannes prématurées 
              et entraîner une surconsommation de produits de traitement.
            </p>
          </div>
        </div>

        {/* Conseil avant commande */}
        <div className="mt-6 bg-emerald-50 rounded-xl border border-emerald-200 p-5" data-testid="advice-section">
          <h3 className="font-semibold text-slate-900 text-sm mb-2 flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
            Conseil avant commande
          </h3>
          <p className="text-slate-700 text-sm leading-relaxed">
            En cas de doute, <strong>vérifiez les dimensions exactes de votre ancien filtre</strong> avant commande. 
            Contactez-nous pour obtenir de l'aide dans l'identification du modèle compatible.
          </p>
        </div>

        {/* Cross-links catégories */}
        <div className="mt-8 grid md:grid-cols-3 gap-4">
          <Link to="/pieces-detachees-spa" className="bg-white rounded-xl border border-slate-200 p-4 hover:border-[#1E3A5F]/30 hover:shadow-md transition-all flex items-center gap-3">
            <div className="p-2 bg-slate-100 rounded-lg">
              <ArrowRight className="w-5 h-5 text-slate-600" />
            </div>
            <div>
              <span className="font-semibold text-slate-900 text-sm">Pièces détachées</span>
              <p className="text-xs text-slate-500">Pompes, réchauffeurs...</p>
            </div>
          </Link>
          <Link to="/entretien-spa" className="bg-white rounded-xl border border-slate-200 p-4 hover:border-[#1E3A5F]/30 hover:shadow-md transition-all flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <ArrowRight className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <span className="font-semibold text-slate-900 text-sm">Entretien de spa</span>
              <p className="text-xs text-slate-500">Produits de traitement...</p>
            </div>
          </Link>
          <Link to="/accessoires-spa" className="bg-white rounded-xl border border-slate-200 p-4 hover:border-[#1E3A5F]/30 hover:shadow-md transition-all flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <ArrowRight className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <span className="font-semibold text-slate-900 text-sm">Accessoires spa</span>
              <p className="text-xs text-slate-500">Escaliers, couvertures...</p>
            </div>
          </Link>
        </div>

        {/* CTA Contact */}
        <ContactCTA category="Filtres spa" />

        {/* CTA final */}
        <div className="mt-10 bg-gradient-to-r from-[#1E3A5F] to-[#2d5a8f] rounded-2xl p-6 sm:p-8 text-center">
          <h2 className="text-xl sm:text-2xl font-bold text-white mb-3">
            Besoin d'aide pour identifier votre filtre ?
          </h2>
          <p className="text-slate-300 mb-5 max-w-xl mx-auto text-sm">
            Notre équipe peut vous aider à trouver le filtre compatible avec votre spa
          </p>
          <a href={`mailto:${EMAIL}`}>
            <Button size="lg" className="bg-[#F97316] hover:bg-[#EA580C] text-white font-bold gap-2">
              <Mail className="w-5 h-5" />
              Nous contacter
            </Button>
          </a>
        </div>
      </div>
    </div>
  );
};

export default FiltresSpaPage;
